import React, { useEffect, useState } from 'react'
import type { OptionsDocument, PresentationOptionV2, PresentationPreview, QuoteDocument } from 'paintscout'
import classnames from 'classnames'
import { QuoteContextProvider, useQuote } from '../../context'
import {
  calculateQuote,
  getPresentationLogo,
  getPresentationPreviewQuote,
  CLOSED_STATUSES
} from '@paintscout/util/builder'
import CustomerViewContents from '../CustomerViewContents'
import {
  createTheme,
  ThemeProvider,
  makeStyles,
  CssBaseline,
  CircularProgress,
  jssPreset,
  StylesProvider,
  PdfOwnerDocumentProvider
} from '@paintscout/ui'
import { ClientOptionsProvider, DialogStackProvider, useClientOptions, useUser } from '@paintscout/ui-business'
import { CVLogo, SpinningLogo } from '../components'
import { create } from 'jss'
import Frame, { FrameContextConsumer } from 'react-frame-component'
import isEqual from 'lodash/isEqual'
import type { MuiTheme as Theme } from '@paintscout/ui'
import imgComparisonSliderSource from 'img-comparison-slider/dist/index.js?raw'

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative'
    },
    desktopPreviewRoot: {
      flexGrow: 1
    },
    deviceWrapper: {
      // this is used to create the pseudo element for the notch since frames cannot have PEs
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      '&::before': {
        zIndex: 4,
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto',
        top: 10,
        width: 40,
        height: 4,
        backgroundColor: theme.palette.common.white
      }
    },
    deviceFrame: {
      border: '8px solid',
      borderTop: '24px solid',
      borderColor: theme.palette.grey[400],
      borderRadius: theme.borderRadius['lg']
    },
    minimalWrapper: {
      width: '100%',
      textAlign: 'center'
    },
    minimalFrame: {
      border: `1px solid ${theme.palette.grey[400]}`
    },
    noFrame: {
      border: 'none'
    },
    loadingScreen: {
      alignItems: 'center',
      background: 'white',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      transition: 'top 1000ms cubic-bezier(0.76, 0, 0.24, 1), opacity 1000ms cubic-bezier(0.32, 0, 0.67, 0)',
      top: 0,
      pointerEvents: 'none',
      position: 'absolute',
      width: '100%',
      opacity: 1
    },
    appLoadingDone: {
      opacity: 0
    },
    cvLoadingDone: {
      top: '-100%'
    }
  }),
  { name: 'PresentationImbedPreview' }
)

export interface PresentationPreviewProps {
  border: 'minimal' | 'device' | 'none'
  loadingStyle: 'app' | 'cv' | 'none'
  classes?: any
  presentation?: PresentationOptionV2
  preview?: PresentationPreview
  quoteId?: string
  logoSrc?: string
  handleAcceptClick?: (ev: React.MouseEvent<HTMLElement, MouseEvent>, selectedOptionalItems?: string[]) => void
  isPreviewAccept?: boolean
  onDismissDialog?: (event: any, reason: 'backdropClick' | 'escapeKeyDown') => void
  autoHeight?: boolean
  autoHeightCallback?: React.Dispatch<React.SetStateAction<number>>
  hideFooter?: boolean
  hideNav?: boolean
  preventScroll?: boolean
  usePresentationFromProp?: boolean
  teaserProps?: {
    options: OptionsDocument
    quote: QuoteDocument
    loading?: boolean
    banner?: React.ReactNode
  }
}

const PresentationImbedPreview = (props: PresentationPreviewProps) => {
  const classes = useStyles(props)
  const {
    autoHeight: useAutoHeight,
    autoHeightCallback,
    border,
    handleAcceptClick,
    hideFooter,
    hideNav,
    isPreviewAccept,
    loadingStyle,
    logoSrc,
    onDismissDialog,
    presentation,
    preventScroll,
    preview,
    usePresentationFromProp,
    teaserProps
  } = props
  // have two quote options so it works w/ and w/o context
  const { quote: contextQuote, estimator } = useQuote()
  const initQuote = teaserProps?.quote ?? contextQuote ?? getPresentationPreviewQuote()
  const [quote, setQuote] = useState(initQuote)
  const [selectedOptionalItems, setSelectedOptionalItems] = useState<string[]>([])
  const { options: contextOptions } = useClientOptions()
  const options = teaserProps?.options ?? contextOptions
  const { isCustom } = getPresentationLogo({ options, quote })
  const { user } = useUser()
  const companyId = user?.app_metadata?.companyId
  const isCustomLogo = isCustom && companyId

  const baseTheme = createTheme()

  const [navOpen, setNavOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState<string>(presentation.pages[0].key)
  const [autoHeight, setAutoHeight] = useState(0)
  const [loading, setLoading] = useState(true)
  const [minDurationReached, setMinDurationReached] = useState(false)

  useEffect(() => {
    setTimeout(() => {
      setMinDurationReached(true)
    }, 2600)
  }, [])
  const showLoading = loadingStyle === 'cv' ? !minDurationReached || loading || teaserProps?.loading : loading

  let width: number | string, height: number | string

  switch (preview) {
    case 'mobile':
      width = 391
      height = 622
      break

    case 'tablet':
      width = 799
      height = 1024
      break

    case 'desktop':
      width = '100%'
      height = '100%'
      break

    default:
      width = '100%'
      height = '100%'
      break
  }

  function getHeight(height: number) {
    if (!useAutoHeight) return null
    if (autoHeightCallback) autoHeightCallback(height)
    setAutoHeight(height)
  }

  let interval
  let attempts = 0
  const checkFont = (window: Window) => {
    attempts++
    if (!window.onload) {
      window.onload = () => {}
    }
    const fontsLoaded = window.sessionStorage?.getItem('fontsLoaded')
    if (fontsLoaded || attempts > 99) {
      setLoading(false)
      clearInterval(interval)
    }
  }

  const deviceWidth = width === '100%' ? width : parseInt(width.toString()) + 16
  const deviceHeight = height === '100%' ? height : parseInt(height.toString()) + 32

  function getLoadingIcon() {
    if (loadingStyle === 'app') {
      return <SpinningLogo scale={0.3} />
    } else if (loadingStyle === 'cv') {
      return isCustomLogo ? (
        <CircularProgress size={50} />
      ) : (
        <CVLogo customLogoSrc={logoSrc} fallback={<SpinningLogo scale={0.3} />} label={'Loading...'} />
      )
    } else {
      return null
    }
  }

  useEffect(() => {
    // reset quote when status changes from closed to open, e.g., quote is accepted
    if (!CLOSED_STATUSES.includes(quote.status.value) && CLOSED_STATUSES.includes(initQuote.status.value)) {
      setSelectedOptionalItems([])
      setQuote(initQuote)
    }
  }, [initQuote.status.value])

  const onSelectOptionalItems = (keys: string[]) => {
    const newQuote = calculateQuote({ quote, options })
    if (!isEqual(newQuote.totals, quote.totals)) {
      setSelectedOptionalItems(keys)
      setQuote(newQuote)
    }
  }

  const handleAcceptWithOptionalItems = (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
    handleAcceptClick(ev, selectedOptionalItems)
  }

  return (
    <div className={classnames(classes.root, { [classes.desktopPreviewRoot]: preview === 'desktop' })}>
      <div
        className={classnames({
          [classes.deviceWrapper]: border === 'device',
          [classes.minimalWrapper]: border === 'minimal' || border === 'none'
        })}
      >
        <div
          style={{
            width: deviceWidth,
            height: useAutoHeight ? autoHeight : deviceHeight,
            position: 'relative',
            overflow: 'hidden'
          }}
          className={classnames({
            [classes.deviceFrame]: border === 'device',
            [classes.minimalFrame]: border === 'minimal',
            [classes.noFrame]: border === 'none'
          })}
        >
          {loadingStyle !== 'none' && (
            <div
              className={classnames(classes.loadingScreen, {
                [classes.appLoadingDone]: !showLoading && loadingStyle === 'app',
                [classes.cvLoadingDone]: !showLoading && loadingStyle === 'cv'
              })}
            >
              {getLoadingIcon()}
            </div>
          )}
          <Frame
            id={'component-frame'}
            style={{ border: 'none' }}
            width={width}
            height={useAutoHeight ? autoHeight : height}
          >
            <FrameContextConsumer>
              {({ document, window }) => {
                if (!interval) {
                  interval = setInterval(checkFont, 100, window as any)
                }

                const jss = create({
                  plugins: [...jssPreset().plugins],
                  insertionPoint: document.head
                })

                const styleEl = document.getElementById('appStyles')
                if (!styleEl) {
                  const style = document.createElement('style')
                  style.id = 'appStyles'
                  style.appendChild(document.createTextNode(appStyles()))
                  document.head.appendChild(style)
                }

                // The iframe is a fresh document: it gets neither the parent page's Google
                // Fonts <link> (Inter) nor its bundled @font-face rules (Euclid, CustomFont-SW).
                // Euclid's woff2 files are Vite-hashed assets with no stable URL, so copy the
                // parent's resolved @font-face rules instead of hardcoding paths. CustomFont-SW
                // (Sherwin-Williams partner sections, #21856) is included here too — without it
                // SW headings silently fall back to a generic font inside the preview iframe.
                const parentFontsEl = document.getElementById('parentFontFaces')
                if (!parentFontsEl) {
                  const css = getParentFontFaceCss(['Euclid', 'CustomFont-SW'])
                  if (css) {
                    const style = document.createElement('style')
                    style.id = 'parentFontFaces'
                    style.appendChild(document.createTextNode(css))
                    document.head.appendChild(style)
                  }
                }

                const interLinkEl = document.getElementById('interFontLink')
                if (!interLinkEl) {
                  const link = document.createElement('link')
                  link.id = 'interFontLink'
                  link.rel = 'stylesheet'
                  link.href =
                    'https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap'
                  document.head.appendChild(link)
                }

                // The SliderImage section's <img-comparison-slider> custom element only ever
                // registers against whichever window/document first executed the app bundle —
                // the parent page, not this iframe. Custom element classes can't be re-registered
                // across realms (the class's HTMLElement base is tied to the realm it was defined
                // in), so without this the element never upgrades here and silently renders as
                // two stacked plain images with no slider behavior. Re-running the library's own
                // source as a real <script> inside this document defines it fresh against the
                // iframe's own window, exactly as if the iframe had loaded the bundle itself.
                const sliderScriptEl = document.getElementById('imgComparisonSliderScript')
                if (!sliderScriptEl) {
                  const script = document.createElement('script')
                  script.id = 'imgComparisonSliderScript'
                  script.textContent = imgComparisonSliderSource
                  document.head.appendChild(script)
                }

                if (preventScroll) {
                  const body = document.getElementsByTagName('body')[0]
                  body.style.overflow = 'hidden'
                }

                function handlePageClick(ev: React.MouseEvent, key: string) {
                  setCurrentPage(key)
                  setNavOpen(false)

                  // The nav emits a page key when multi-page and a section key when single-page
                  // (#21990). A section is on the page already on screen, so scroll to it; a page
                  // swaps what renders, so there's nothing to scroll to — go to the top.
                  const target = document.getElementById(key)
                  if (target) {
                    window.scrollTo({ top: (target.offsetTop ?? 0) + 24, behavior: 'smooth' })
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                }

                return (
                  <StylesProvider jss={jss}>
                    <ThemeProvider theme={baseTheme}>
                      {/*
                        PdfOwnerDocumentProvider wraps DialogStackProvider (not the other way
                        around): DialogStackProvider renders its open-dialog stack as a sibling of
                        its children, not a descendant (see the same gotcha documented in
                        apps/customer-view/src/components/ViewProvider.tsx), so a dialog opened
                        here (e.g. ViewMediaDialog's PDF viewer) would otherwise never see the
                        iframe's document and would fall back to the parent document for pdf.js's
                        font loading — reintroducing the tofu-glyph bug for dialogs specifically.
                      */}
                      <PdfOwnerDocumentProvider document={document}>
                        <DialogStackProvider>
                          <ClientOptionsProvider options={options}>
                            <CssBaseline />
                            <QuoteContextProvider
                              quote={{ ...quote, presentationTemplate: presentation }}
                              estimator={estimator}
                              handleOptionalItems={{ onSelectOptionalItems, selectedOptionalItems }}
                            >
                              <CustomerViewContents
                                allowResponse={!!handleAcceptClick}
                                banner={teaserProps?.banner}
                                currentPage={currentPage}
                                getHeight={getHeight}
                                handleAcceptClick={handleAcceptClick ? handleAcceptWithOptionalItems : null}
                                handlePageClick={(ev, key) => handlePageClick(ev, key)}
                                handleSectionClick={() => null}
                                hideFooter={hideFooter}
                                hideNav={hideNav}
                                isInvoice={quote?.is_invoice}
                                isPreview={true}
                                isPreviewAccept={isPreviewAccept}
                                loadAllFonts={true}
                                navOpen={navOpen}
                                onDismissDialog={onDismissDialog}
                                presentation={presentation}
                                preview={preview}
                                setNavOpen={setNavOpen}
                                usePresentationFromProp={usePresentationFromProp}
                                view={'quote'}
                              />
                            </QuoteContextProvider>
                          </ClientOptionsProvider>
                        </DialogStackProvider>
                      </PdfOwnerDocumentProvider>
                    </ThemeProvider>
                  </StylesProvider>
                )
              }}
            </FrameContextConsumer>
          </Frame>
        </div>
      </div>
    </div>
  )
}

export default PresentationImbedPreview

/**
 * Serialize the parent document's @font-face rules for the given families so they can
 * be re-declared inside the preview iframe. CSSOM's cssText carries the resolved
 * (bundler-hashed) font URLs, which have no stable path we could hardcode.
 *
 * NOTE: runs in the parent window scope — `document` here is the parent document,
 * not the iframe's.
 */
function getParentFontFaceCss(families: string[]): string {
  const rules: string[] = []
  try {
    for (const sheet of Array.from(document.styleSheets)) {
      let cssRules: CSSRuleList
      try {
        cssRules = sheet.cssRules
      } catch {
        continue // cross-origin stylesheet (e.g. Google Fonts <link>) — not readable
      }
      for (const rule of Array.from(cssRules)) {
        if (rule.type === CSSRule.FONT_FACE_RULE) {
          const family = (rule as CSSFontFaceRule).style.getPropertyValue('font-family').replace(/['"]/g, '').trim()
          if (families.includes(family)) {
            rules.push(rule.cssText)
          }
        }
      }
    }
  } catch {
    // font copying must never break the preview itself
  }
  return rules.join('\n')
}

function appStyles() {
  // duplicate of apps/stickybid/src/index.css
  return `
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: normal;
    font-weight: 300;
    src: url('/fonts/degular-300.woff2') format('woff2'), url('/fonts/degular-300.woff') format('woff');
  }
  
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: italic;
    font-weight: 300;
    src: url('/fonts/degular-300-italic.woff2') format('woff2'),
      url('/fonts/degular-300-italic.woff') format('woff');
  }
  
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: normal;
    font-weight: 400;
    src: url('/fonts/degular-400.woff2') format('woff2'), url('/fonts/degular-400.woff') format('woff');
  }
  
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: italic;
    font-weight: 400;
    src: url('/fonts/degular-400-italic.woff2') format('woff2'),
      url('/fonts/degular-400-italic.woff') format('woff');
  }
  
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: normal;
    font-weight: 500;
    src: url('/fonts/degular-500.woff2') format('woff2'), url('/fonts/degular-500.woff') format('woff');
  }
  
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: italic;
    font-weight: 500;
    src: url('/fonts/degular-500-italic.woff2') format('woff2'),
      url('/fonts/degular-500-italic.woff') format('woff');
  }
  
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: normal;
    font-weight: 600;
    src: url('/fonts/degular-600.woff2') format('woff2'), url('/fonts/degular-600.woff') format('woff');
  }
  
  @font-face {
    font-family: 'degular';
    font-display: auto;
    font-style: italic;
    font-weight: 600;
    src: url('/fonts/degular-600-italic.woff2') format('woff2'),
      url('/fonts/degular-600-italic.woff') format('woff');
  }
  
  html {
    font-size: 16px;
  
    /* font-family: 'Roboto', Arial, Helvetica, sans-serif; */
    font-family: 'degular', sans-serif;
  }
  
  body {
    line-height: 1.42857143;
  }
  
  /* :focus-visible polyfill to remove hide focus when interacted with mouse */
  button:focus:not(.focus-visible) {
    outline: none;
  }
  
  @media print {
    body {
      min-width: 1024px;
      border-bottom: 1px solid transparent;
    }
  }
  
  strong {
    font-weight: 500;
  }
  
  fieldset {
    border: 0;
    padding: 0.01em 0 0 0;
    margin: 0;
    min-width: 0;
  }
  
 `
}
